import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  AppBar,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import api from '../api/api';
import { obtenerDatosToken } from '../utils/authToken';

const CatalogosPremios = () => {
  const [incentivos, setIncentivos] = useState([]);
  const [originalIncentivos, setOriginalIncentivos] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedIncentivo, setSelectedIncentivo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    id_pet: '',
    nombre: '',
    descripcion: '',
    puntos_requeridos: 0,
    stock: 0,
    activo: true,
  });
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userIdPet, setUserIdPet] = useState(null);
  const [userIdRol, setUserIdRol] = useState(null);
  const [userSucursales, setUserSucursales] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Cargar token
  useEffect(() => {
    const tokenData = obtenerDatosToken();
    if (tokenData) {
      setUserIdPet(tokenData.id_pet || null);
      setUserIdRol(tokenData.id_rol || null);
      setUserSucursales(tokenData.sucursales || []);
    } else {
      const errorMsg = 'No se encontró un token de usuario';
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const normalizeIncentivo = (item) => {
    if (!item || !item.id_incentivo) return null;
    return {
      id_incentivo: item.id_incentivo,
      id_pet: item.id_pet ?? null,
      nombre: item.nombre ?? item.nombre_incentivo ?? '',
      descripcion: item.descripcion ?? '',
      puntos_requeridos: item.puntos_requeridos ?? 0,
      stock: item.stock ?? 0,
      activo: Boolean(item.activo),
      createdAt: item.createdAt ?? null,
      updatedAt: item.updatedAt ?? null,
    };
  };

  // Cargar Incentivos y Pets
  useEffect(() => {
    const fetchData = async () => {
      if (!userIdPet || !userIdRol) return;
      try {
        setLoading(true);
        const response = await api.get('/puntos-incentivos/?solo_activos=false');
        const rawData = Array.isArray(response.data) ? response.data : [];
        const allowedPets = [userIdPet, ...userSucursales];
        
        const normalized = rawData
          .map(normalizeIncentivo)
          .filter(item => item !== null && allowedPets.includes(item.id_pet));

        setIncentivos(normalized);
        setOriginalIncentivos(normalized);

        const petsRes = await api.get('/pets/');
        setPets(Array.isArray(petsRes.data) ? petsRes.data.filter(pet => allowedPets.includes(pet.id_pet)) : []);
        setLoading(false);
      } catch (err) {
        console.error('Error al cargar incentivos:', err);
        const errorMsg = 'Error al cargar incentivos: ' + (err.response?.data?.detail || err.message);
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        setLoading(false);
      }
    };
    fetchData();
  }, [userIdPet, userIdRol, userSucursales]);

  // Búsqueda
  useEffect(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      setIncentivos(originalIncentivos);
      setPage(0);
      return;
    }
    const filtered = originalIncentivos.filter(item =>
      item.nombre_incentivo.toLowerCase().includes(searchTerm.toLowerCase().trim())
    );
    setIncentivos(filtered);
    setPage(0);
  }, [searchTerm, originalIncentivos]);

  const handleOpenModal = (incentivo = null) => {
    setSelectedIncentivo(incentivo);
    setFormData(
      incentivo
        ? normalizeIncentivo(incentivo) || {
            id_pet: userIdPet || '',
            nombre_incentivo: '',
            descripcion: '',
            puntos_requeridos: 0,
            stock: 0,
            activo: true,
          }
        : {
            id_pet: userIdPet || '',
            nombre_incentivo: '',
            descripcion: '',
            puntos_requeridos: 0,
            stock: 0,
            activo: true,
          }
    );
    setOpenModal(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Guardar (Crear o Editar)
  const handleSave = async () => {
    try {
      if (!formData.nombre || !formData.id_pet || formData.puntos_requeridos <= 0) {
        const errorMsg = 'Campos obligatorios incompletos o puntos inválidos.';
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        return;
      }

      const payload = {
        id_pet: Number(formData.id_pet),
        nombre: formData.nombre, // Se envía 'nombre'
        descripcion: formData.descripcion || null,
        puntos_requeridos: Number(formData.puntos_requeridos),
        stock: Number(formData.stock),
        activo: Boolean(formData.activo),
      };

      if (selectedIncentivo) {
        const response = await api.put(`/puntos-incentivos/${selectedIncentivo.id_incentivo}`, payload);
        const updated = normalizeIncentivo(response.data);
        if (updated) {
          const updatedList = incentivos.map(i => (i.id_incentivo === selectedIncentivo.id_incentivo ? updated : i));
          setIncentivos(updatedList);
          setOriginalIncentivos(updatedList);
          setSnackbarMessage('Premio actualizado exitosamente');
          setSnackbarOpen(true);
        }
      } else {
        const response = await api.post('/puntos-incentivos/', payload);
        const newIncentivo = normalizeIncentivo(response.data);
        if (newIncentivo) {
          const newList = [...incentivos, newIncentivo];
          setIncentivos(newList);
          setOriginalIncentivos(newList);
          setSnackbarMessage('Premio creado exitosamente');
          setSnackbarOpen(true);
        }
      }
      setOpenModal(false);
      setError(null);
      setPage(0);
    } catch (err) {
      console.error('Error al guardar premio:', err);
      
      // Formatear correctamente el error 422 de FastAPI
      let errorMsg = 'Error al guardar premio';
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMsg += ': ' + err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          errorMsg += ': ' + err.response.data.detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join(', ');
        }
      } else if (err.message) {
        errorMsg += ': ' + err.message;
      }

      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Alternar Activo/Inactivo
  const handleToggleActivo = async (id, estadoActual) => {
    try {
      const item = incentivos.find(i => i.id_incentivo === id);
      if (!item) return;

      const nuevoEstado = !estadoActual;
      const payload = {
        id_pet: item.id_pet,
        nombre_incentivo: item.nombre_incentivo,
        descripcion: item.descripcion,
        puntos_requeridos: item.puntos_requeridos,
        stock: item.stock,
        activo: nuevoEstado,
      };

      const response = await api.put(`/puntos-incentivos/${id}`, payload);
      const updated = normalizeIncentivo(response.data);

      if (updated) {
        const updatedList = incentivos.map(i => (i.id_incentivo === id ? updated : i));
        setIncentivos(updatedList);
        setOriginalIncentivos(updatedList);
        setSnackbarMessage(`Premio ${updated.activo ? 'activado' : 'desactivado'} exitosamente`);
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Error al actualizar estado:', err);
      const errorMsg = 'Error al actualizar estado: ' + (err.response?.data?.detail || err.message);
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Eliminar
  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este premio del catálogo?')) return;
    try {
      await api.delete(`/puntos-incentivos/${id}`);
      const updatedList = incentivos.filter(i => i.id_incentivo !== id);
      setIncentivos(updatedList);
      setOriginalIncentivos(updatedList);
      setSnackbarMessage('Premio eliminado exitosamente');
      setSnackbarOpen(true);
      setPage(0);
    } catch (err) {
      console.error('Error al eliminar premio:', err);
      const errorMsg = 'Error al eliminar premio: ' + (err.response?.data?.detail || err.message);
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  const paginatedIncentivos = incentivos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <AppBar position="relative" sx={{ bgcolor: '#1e293b', mb: 3 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Catálogo de Premios e Incentivos
          </Typography>
          <TextField
            variant="outlined"
            placeholder="Buscar premio..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ bgcolor: 'white', borderRadius: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth={false} disableGutters>
        {loading || !userIdPet || !userIdRol ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : incentivos.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6">Sin premios registrados</Typography>
            <Typography color="text.secondary">
              Agrega premios o productos canjeables para tus clientes.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              sx={{ mt: 2 }}
            >
              Nuevo Premio
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenModal()}
                >
                  Nuevo Premio
                </Button>
              </Box>

              <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', boxShadow: 3 }}>
                <Table sx={{ tableLayout: 'auto' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Nombre Premio</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Descripción</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Puntos Requeridos</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Stock</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Veterinaria</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Activo</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Creado</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedIncentivos.map((row) => (
                      <TableRow key={row.id_incentivo}>
                        <TableCell sx={{ textAlign: 'center' }}>{row.id_incentivo}</TableCell>
                        <TableCell sx={{ textAlign: 'center', fontWeight: 'bold' }}>{row.nombre}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>{row.descripcion || 'N/A'}</TableCell>
                        <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', color: 'primary.main' }}>
                          {row.puntos_requeridos} pts
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>{row.stock}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          {pets.find(p => p.id_pet === row.id_pet)?.nombre_veterinaria || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Switch
                            checked={row.activo}
                            onChange={() => handleToggleActivo(row.id_incentivo, row.activo)}
                            color="primary"
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>{formatDate(row.createdAt)}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <IconButton onClick={() => handleOpenModal(row)} color="primary">
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(row.id_incentivo)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
                <Button disabled={page === 0} onClick={() => setPage(page - 1)} variant="contained">
                  Anterior
                </Button>
                <Typography sx={{ alignSelf: 'center' }}>
                  Página {page + 1} de {Math.ceil(incentivos.length / rowsPerPage)}
                </Typography>
                <Button
                  disabled={page >= Math.ceil(incentivos.length / rowsPerPage) - 1}
                  onClick={() => setPage(page + 1)}
                  variant="contained"
                >
                  Siguiente
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Modal Crear / Editar */}
        <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="sm">
          <DialogTitle>{selectedIncentivo ? 'Editar Premio' : 'Nuevo Premio'}</DialogTitle>
          <DialogContent>
            <TextField
              label="Nombre del Premio"
              fullWidth
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              sx={{ mt: 2 }}
              required
            />
            <TextField
              label="Descripción"
              fullWidth
              multiline
              rows={2}
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              sx={{ mt: 2 }}
            />
            <TextField
              label="Puntos Requeridos"
              type="number"
              fullWidth
              value={formData.puntos_requeridos}
              onChange={(e) => setFormData({ ...formData, puntos_requeridos: e.target.value })}
              sx={{ mt: 2 }}
              required
            />
            <TextField
              label="Stock Disponible"
              type="number"
              fullWidth
              value={formData.stock}
              onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
              sx={{ mt: 2 }}
              required
            />
            <TextField
              select
              label="Veterinaria"
              fullWidth
              value={formData.id_pet}
              onChange={(e) => setFormData({ ...formData, id_pet: e.target.value })}
              sx={{ mt: 2 }}
              required
            >
              {pets.map((pet) => (
                <MenuItem key={pet.id_pet} value={pet.id_pet}>
                  {pet.nombre_veterinaria}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={formData.activo}
                onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                color="primary"
              />
              <Typography>Activo para Canje</Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!formData.nombre || !formData.id_pet || Number(formData.puntos_requeridos) <= 0}
            >
              Guardar
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ zIndex: 1500 }}
        >
          <Alert onClose={handleSnackbarClose} severity={error ? 'error' : 'success'} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default CatalogosPremios;